import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { getSilentCredential } from "@/lib/fedcm";

interface SilentAuthOptions {
  /** Whether silent re-auth is enabled */
  enabled?: boolean;
  /** How often to check token freshness (ms). Default: 5 minutes */
  checkIntervalMs?: number;
  /** How far before access token expiry to attempt refresh (ms). Default: 2 minutes */
  refreshBufferMs?: number;
}

/**
 * Proactively refreshes the session via the native IdentityCredential API
 * (FedCM) before the refresh token expires. Provides seamless session
 * extension for Google-authenticated users with no UI interaction.
 *
 * Uses getSilentCredential() which tries:
 *   1. IdentityCredential (Chrome 108+ FedCM native) → returns a Google ID token
 *      → exchanges for fresh Prezntz access/refresh tokens
 *   2. Falls through silently on unsupported browsers (401 handling via axios interceptor)
 *
 * This approach avoids re-calling google.accounts.id.initialize(), which
 * would override the shared GIS singleton used by One Tap / rendered button.
 */
export function useGoogleSilentAuth(options: SilentAuthOptions = {}) {
  const {
    enabled = true,
    checkIntervalMs = 5 * 60 * 1000, // 5 minutes
    refreshBufferMs = 2 * 60 * 1000, // 2 minutes before expiry
  } = options;

  const { isAuthenticated, user } = useAuth();
  const silentRefreshInProgress = useRef(false);

  useEffect(() => {
    if (!enabled || !isAuthenticated) return;
    if (user?.authProvider !== "GOOGLE") return;

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    const getTokenExpiry = (): number | null => {
      const token = localStorage.getItem("access_token");
      if (!token) return null;
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.exp ? payload.exp * 1000 : null;
      } catch {
        return null;
      }
    };

    const attemptSilentGoogleRefresh = async () => {
      if (silentRefreshInProgress.current) return;
      silentRefreshInProgress.current = true;

      try {
        const credential = await getSilentCredential();

        // Only IdentityCredential gives us a token we can use for re-auth
        if (credential?.type !== "identity" || !credential.token) return;

        const res = await fetch(`${import.meta.env.VITE_API_URL || ""}/api/v1/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken: credential.token }),
        });

        if (res.ok) {
          const data = await res.json();
          const { accessToken, refreshToken } = data.data;
          localStorage.setItem("access_token", accessToken);
          localStorage.setItem("refresh_token", refreshToken);
        }
      } catch {
        // Silent failure — axios interceptor will handle 401s on next request
      } finally {
        silentRefreshInProgress.current = false;
      }
    };

    const checkAndRefresh = () => {
      const expiry = getTokenExpiry();
      if (!expiry) return;

      const timeUntilExpiry = expiry - Date.now();

      // Proactively re-auth via FedCM before the access token expires.
      // This avoids the reactive 401 cycle entirely for Google users — we get
      // fresh Prezntz tokens silently without any user interaction or visible error.
      if (timeUntilExpiry < refreshBufferMs && timeUntilExpiry > 0) {
        attemptSilentGoogleRefresh();
      }
    };

    checkAndRefresh();
    const interval = setInterval(checkAndRefresh, checkIntervalMs);

    return () => {
      clearInterval(interval);
    };
  }, [enabled, isAuthenticated, user?.authProvider, checkIntervalMs, refreshBufferMs]);
}
